import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { Box, CircularProgress, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { voiceToneOptions } from "./VideoSuiteHelper";
import { useEffect, useMemo, useState } from "react";
import { PlayToneIcon } from "../../../../Custom-imeges/VideoSuite/PlayToneIcon";
import { SelectOptionBreadcrumbs } from "./SelectOptionBreadcrumbs";
import {
  AvatarVideoData,
  AvatarVoiceVideoData,
} from "../../../../Types/VideoSuite-Types";
import { SearchInput } from "../../../../shared/SearchInput";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  bgcolor: "var(--sub_background6)",
  boxShadow: 24,
  outline: "none",
  borderRadius: "12px",
};

interface SelectAvatarModalProps {
  open: boolean;
  handleClose: () => void;
  handleSelectVoiceTone: (voice: AvatarVoiceVideoData) => void;
  selectedVoiceTone: AvatarVoiceVideoData | undefined;
  selectedAvatar: AvatarVideoData | undefined;
  avatarVoices: AvatarVoiceVideoData[];
  isLoading: boolean;
}

export const SelectVoiceToneModal = ({
  open,
  handleClose,
  handleSelectVoiceTone,
  selectedVoiceTone,
  avatarVoices,
  selectedAvatar,
  isLoading,
}: SelectAvatarModalProps) => {
  const [searchInputValue, setSearchInputValue] = useState("");
  const [selectedToneOption, setSelectedToneOption] = useState(
    voiceToneOptions[0]
  );
  const [playingAudio, setPlayingAudio] = useState<HTMLAudioElement | null>(
    null
  );
  const [currentlySelectedVoice, setCurrentlySelectedVoice] = useState<
    AvatarVoiceVideoData | undefined
  >();
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(
    null
  );

  const handleUseSelectedTone = (voice: AvatarVoiceVideoData | undefined) => {
    if (voice) {
      handleSelectVoiceTone(voice);
      setCurrentlySelectedVoice(voice);
      handleCloseWithCleanup();
    }
  };

  const handlePlayVoice = (voice: AvatarVoiceVideoData) => {
    // If same voice is playing, stop it
    if (playingAudio && currentlyPlayingId === voice.voice_id) {
      playingAudio.pause();
      playingAudio.currentTime = 0;
      setCurrentlyPlayingId(null);
      return;
    }

    // Stop previous audio
    if (playingAudio) {
      playingAudio.pause();
      playingAudio.currentTime = 0;
    }

    // Create new audio
    const newAudio = new Audio(voice.preview_audio);

    setPlayingAudio(newAudio);
    setCurrentlyPlayingId(voice.voice_id);
    const playPromise = newAudio.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        setCurrentlyPlayingId(null);
      });
    }

    // Reset when audio ends
    newAudio.onended = () => {
      setCurrentlyPlayingId(null);
    };
  };

  const handleStActiveVoice = (voice: AvatarVoiceVideoData) => {
    setCurrentlySelectedVoice(voice);
    handlePlayVoice(voice);
  };

  const handleCloseWithCleanup = () => {
    if (playingAudio) {
      playingAudio.pause();
      playingAudio.currentTime = 0;
      setPlayingAudio(null);
      setCurrentlyPlayingId(null);
    }
    handleClose();
    setSearchInputValue("");
  };

  const filteredVoiceTones = useMemo(() => {
    if (!searchInputValue) {
      return avatarVoices.filter(
        (voice) =>
          voice.gender.toLowerCase() === selectedAvatar?.gender.toLowerCase()
      );
    } else {
      return avatarVoices.filter(
        (voice) =>
          voice.gender.toLowerCase() === selectedAvatar?.gender.toLowerCase() &&
          voice.name.toLowerCase().includes(searchInputValue.toLowerCase())
      );
    }
  }, [selectedAvatar, searchInputValue]);

  // Change the selected tone gender when selected avatar gender change.
  useEffect(() => {
    if (filteredVoiceTones) {
      handleUseSelectedTone(filteredVoiceTones[0]);
    }
  }, [selectedAvatar?.gender]);

  return (
    <Modal
      open={open}
      onClose={handleCloseWithCleanup}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...style,
          "@media (max-width: 900px)": {
            width: "98%",
          },
        }}
      >
        <Box className="avatar-modal-header">
          <Typography>Choose voice</Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={handleCloseWithCleanup}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        <Box
          className="avatar-modal-body"
          sx={{ padding: "16px 34px 24px 34px" }}
        >
          <Box className="avatar-modal-body-header">
            <Box>
              <SelectOptionBreadcrumbs
                options={voiceToneOptions}
                selectedOption={selectedToneOption}
                handleSelectOption={setSelectedToneOption}
              />
            </Box>
            <Box className="search-box-container">
              <SearchInput
                searchInputValue={searchInputValue}
                setSearchInputValue={setSearchInputValue}
                placeholder="Search Voices"
              />
            </Box>
          </Box>

          <Box className="avatar-voices-container">
            {isLoading && searchInputValue ? (
              <Box className="empty-message">
                <CircularProgress sx={{ color: "#12b6ea" }} />
              </Box>
            ) : filteredVoiceTones?.length > 0 ? (
              filteredVoiceTones
                ?.filter(
                  (voice) =>
                    voice.gender.toLowerCase() ===
                    selectedAvatar?.gender.toLocaleLowerCase()
                )
                .map((voice, i) => (
                  <Box
                    key={i}
                    className={`avatar-tone-box ${
                      currentlySelectedVoice?.voice_id === voice.voice_id
                        ? "active-tone"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStActiveVoice(voice);
                    }}
                  >
                    <Box style={{ cursor: "pointer" }}>
                      {currentlyPlayingId === voice.voice_id ? (
                        <img
                          src="/images/VideoSuite/playVoiceGIF.gif"
                          alt="Playing"
                          style={{ width: 24, height: 24 }}
                        />
                      ) : (
                        <PlayToneIcon />
                      )}
                    </Box>
                    <Box className="avatar-tone-info">
                      <Typography className="avatar-tone-name">
                        {voice?.name}
                      </Typography>
                      <Typography className="avatar-tone-gender">
                        {voice?.gender}
                      </Typography>
                    </Box>
                  </Box>
                ))
            ) : (
              <Box className="empty-message">No voices found</Box>
            )}
          </Box>

          <Box className="select-voice">
            <Box
              className="select-voice-btn"
              onClick={() => handleUseSelectedTone(currentlySelectedVoice)}
            >
              Use this voice
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
